import { Button, cn, Separator } from '@lensora/ui'
import {
  BarChart2,
  BookOpen,
  Brain,
  Code2,
  FileText,
  LayoutDashboard,
  LogOut,
  PenLine,
  Search,
  Settings,
  ShieldCheck,
  Users2,
  Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { NavLink } from 'react-router-dom'


import { WebsiteSwitcher } from '@/components/dashboard/WebsiteSwitcher'
import { useMe } from '@/hooks/use-me'
import { useAuth } from '@/providers/auth-provider'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pb-1.5 pt-4 text-[10px] font-semibold uppercase tracking-widest text-text-muted opacity-60">
      {children}
    </p>
  )
}

function SidebarLink({
  to,
  end,
  icon: Icon,
  badge,
  children,
}: {
  to: string
  end?: boolean
  icon: LucideIcon
  badge?: string
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-brand-primary font-medium text-brand-primary-foreground'
            : 'text-text-muted hover:bg-surface-raised hover:text-text'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{children}</span>
      {badge && (
        <span className="rounded-full border border-brand-accent/30 bg-brand-accent/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-accent">
          {badge}
        </span>
      )}
    </NavLink>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const { data } = useMe()
  const { signOut } = useAuth()
  const isLensoraAdmin = data?.user.isLensoraAdmin ?? false

  return (
    <aside className="flex h-full w-60 flex-col border-r border-border bg-surface">
      {/* Brand / logo */}
      <div className="flex h-16 items-center gap-3 px-4">
        <div className="h-8 w-8 rounded bg-brand-primary" />
        <span className="font-semibold text-text">
          {data?.tenant?.name ?? 'Dashboard'}
        </span>
      </div>

      <Separator />

      {/* Website switcher */}
      <div className="px-3 py-2">
        <WebsiteSwitcher />
      </div>

      <Separator />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3">

        {/* ── Visibility ── */}
        <SectionLabel>Visibility</SectionLabel>
        <div className="space-y-0.5">
          <SidebarLink to="/dashboard" end icon={LayoutDashboard}>Dashboard</SidebarLink>
          <SidebarLink to="/dashboard/prompts" icon={Search}>Prompts</SidebarLink>
          <SidebarLink to="/dashboard/competitors" icon={Users2}>Competitors</SidebarLink>
          <SidebarLink to="/dashboard/automation" icon={Zap}>Automation</SidebarLink>
        </div>

        {/* ── Insights ── */}
        <SectionLabel>Insights</SectionLabel>
        <div className="space-y-0.5">
          <SidebarLink to="/dashboard/recommendations" icon={Brain}>Recommendations</SidebarLink>
          <SidebarLink to="/dashboard/sources" icon={BookOpen}>Sources</SidebarLink>
          <SidebarLink to="/dashboard/reports" icon={FileText}>Reports</SidebarLink>
          <SidebarLink to="/dashboard/analytics" icon={BarChart2}>Analytics</SidebarLink>
          <SidebarLink to="/dashboard/content-briefs" icon={PenLine}>Content Briefs</SidebarLink>
          <SidebarLink to="/dashboard/schema-generator" icon={Code2} badge="Growth">Schema Generator</SidebarLink>
        </div>

        {/* ── Account ── */}
        <SectionLabel>Account</SectionLabel>
        <div className="space-y-0.5">
          <SidebarLink to="/dashboard/settings" icon={Settings}>Settings</SidebarLink>
        </div>

        {/* ── Lensora Admin ── */}
        {isLensoraAdmin && (
          <>
            <Separator className="my-3" />
            <div className="space-y-0.5">
              <SidebarLink to="/admin" icon={ShieldCheck}>Lensora Admin</SidebarLink>
            </div>
          </>
        )}
      </nav>

      <Separator />

      {/* User footer */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent text-xs font-medium text-text-inverse">
            {data?.user.email?.charAt(0).toUpperCase() ?? '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-text">
              {data?.user.fullName ?? data?.user.email}
            </p>
            <p className="truncate text-xs capitalize text-text-muted">{data?.user.role}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-text-muted hover:text-text"
            onClick={() => signOut()}
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </aside>
  )
}
